import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from 'src/app-redux/rtkq.interceptor';
import { transformResponses } from 'src/app-redux/rtkq.interceptor';
import { apiResources } from 'src/common/Constants';
import {
  DemandAdda,
  DemandAddaChangePasswordArgs,
  DemandAddaCreateArgs,
  DemandAddaFetchArgs,
  demandAddaKeyValuePairs,
  DemandAddaUpdateArgs,
  DemandAddaUpdateStatusArgs,
} from 'src/management/DemandAdda/demand-adda';
import { BlockReasons } from 'src/management/User';

const demandAddasTag = 'demand-addas';

/** RTK Query API slice for Demand Adda management endpoints. */
export const demandAddasApi = createApi({
  reducerPath: 'demandAddasApi',
  tagTypes: [demandAddasTag],
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    /** Query to fetch Demand Adda projects with pagination and search. */
    fetchDemandAddas: builder.query<DemandAdda[], DemandAddaFetchArgs>({
      providesTags: [demandAddasTag],
      query: (args) => ({
        url: apiResources.demandAdda,
        method: 'GET',
        params: {
          page: args.page,
          pageSize: args.pageSize,
          [args.search.by]: args.search.keyword,
        },
      }),
      ...transformResponses,
    }),
    /** Mutation to create a new Demand Adda project. */
    createDemandAdda: builder.mutation<void, DemandAddaCreateArgs>({
      query: (args) => {
        const kv = demandAddaKeyValuePairs(args);
        const form = new FormData();
        kv.forEach(({ key, value }) => form.append(key, value as any));
        return {
          url: apiResources.demandAdda,
          method: 'POST',
          body: form,
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : [demandAddasTag]),
    }),
    /** Mutation to update an existing Demand Adda project. */
    updateDemandAdda: builder.mutation<void, DemandAddaUpdateArgs>({
      query: (args) => {
        const kv = demandAddaKeyValuePairs(args);
        const form = new FormData();
        kv.forEach(({ key, value }) => form.append(key, value as any));
        return {
          url: `${apiResources.demandAdda}/${args.projectId}`,
          method: 'PUT',
          body: form,
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : [demandAddasTag]),
    }),
    /** Mutation to delete a Demand Adda project by ID. */
    deleteDemandAdda: builder.mutation<string, number>({
      query: (id) => ({
        url: `${apiResources.demandAdda}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error) => (error ? [] : [demandAddasTag]),
      ...transformResponses,
    }),
    /** Query to fetch block reasons for Demand Adda projects. */
    getDemandAddaBlockReasons: builder.query<BlockReasons[], void>({
      query: () => ({
        url: `${apiResources.demandAdda}/blockReasons?reasonType=1&userType=1`,
        method: 'GET',
      }),
      transformErrorResponse: transformResponses.transformErrorResponse,
      transformResponse: (response: { data: BlockReasons[] }) => {
        const data = transformResponses.transformResponse(response);
        data.push({ id: null, reason: 'Other' });
        return data;
      },
    }),
    /** Mutation to update block status for a Demand Adda project. */
    updateDemandAddaBlockStatus: builder.mutation<void, DemandAddaUpdateStatusArgs>({
      query: (args) => {
        return {
          url: `${apiResources.demandAdda}/${args.phoneNumber}/update-status`,
          method: 'POST',
          body: args,
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : [demandAddasTag]),
    }),
    /** Mutation to change the password for a Demand Adda user. */
    changeDemandAddaPassword: builder.mutation<void, DemandAddaChangePasswordArgs>({
      query: (args) => ({
        url: `${apiResources.demandAdda}/${args.defaultUserId}/changePassword`,
        method: 'POST',
        body: {
          password: args.password,
          confirmPassword: args.confirmPassword,
        },
      }),
      invalidatesTags: (_result, error) => (error ? [] : [demandAddasTag]),
    }),
  }),
});

/** RTK Query hooks for Demand Adda management endpoints. */
export const {
  useFetchDemandAddasQuery,
  useCreateDemandAddaMutation,
  useUpdateDemandAddaMutation,
  useDeleteDemandAddaMutation,
  useGetDemandAddaBlockReasonsQuery,
  useUpdateDemandAddaBlockStatusMutation,
  useChangeDemandAddaPasswordMutation,
} = demandAddasApi;
